import { DOCUMENT } from "@angular/common";
import { Injectable, Injector } from "@angular/core";
import { Router } from "@angular/router";
import { ErrorCode, errorMessageKey, KeyOf } from "@ankaadia/ankaadia-shared";
import { ApolloErrorHandler, ApplicationInsightsService, translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { Mutation, Query, StaticDataType } from "@ankaadia/graphql";
import { ErrorResponse } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { AuthService } from "@auth0/auth0-angular";
import { GraphQLFormattedError } from "graphql";
import { zipObject } from "lodash";
import { finalize, forkJoin, map, Observable, of } from "rxjs";
import { environment } from "../../../environments/environment";
import { type Message, MessageService } from "../../features/message/message.service";
import { MessageDialogService } from "../message-dialog/message-dialog.service";
import { ConfigurationReloadService } from "./configuration-reload.service";
import { PromptUpdateService } from "../services/promptUpdateService";
import { StaticDataPipe } from "../static-data/static-data.pipe";

type MutationOperation = KeyOf<Mutation>;
type QueryOperation = KeyOf<Query>;

@Injectable({ providedIn: "root" })
export class ApolloErrorHandlerService implements ApolloErrorHandler {
  private static readonly SESSION_EXPIRED_AUTH0_CODES = ["login_required", "missing_refresh_token", "consent_required"];

  private redirectingToLogin = false;
  /** Never reset: the only way out of the dialog is the reload, which starts the app afresh. */
  private isReloadPromptOpen = false;

  constructor(private readonly injector: Injector) {}

  handle(response: ErrorResponse): void {
    // `this.injector.get` is used in this file so that there's no circular dependency reported
    // on `_Apollo` during the runtime; nobody understands why this is happening, though
    const messageService = this.injector.get(MessageService);
    const appInsights = this.injector.get(ApplicationInsightsService);

    if (this.handleSessionExpired(response)) {
      return;
    }

    response.graphQLErrors?.map((e) => {
      type ErrorHandler = (error: GraphQLFormattedError, response: ErrorResponse) => boolean;
      const errorHandlers: ErrorHandler[] = [
        this.handleForbidden,
        this.handleAiError,
        this.handleUserInputError,
        this.handleConcurrencyViolation,
        this.handleServiceBusy,
        this.handleAggregateError,
        this.handleVersionError,
        this.handleApplicationMaskError,
      ];

      const handled = errorHandlers.map((x) => x.bind(this)).some((x) => x(e, response));

      if (handled) {
        return;
      }

      const extensions: Record<string, any> = e.extensions;
      response.graphQLErrors?.forEach((x) => appInsights.logException(new Error(x.message), response.operation));

      let message = "";
      response.graphQLErrors?.map((val) => (message += val.message + "\n"));
      if (response.networkError?.message) {
        message += response.networkError?.message + "\n";
      }
      if (extensions?.code) {
        message += extensions?.code + "\n";
      }
      if (extensions?.exception?.response?.message) {
        message += extensions?.exception?.response?.message + "\n";
      }
      if (extensions?.exception?.stacktrace) {
        if (extensions?.exception?.stacktrace.join) {
          message += extensions?.exception?.stacktrace.join("\n") + "\n";
        } else {
          message += extensions?.exception?.stacktrace + "\n";
        }
      }

      if (!environment.production) {
        // eslint-disable-next-line no-console
        console.error(message, response);
      }

      messageService.add(this.makeMessage(response.operation.operationName, message));
    });

    if (response.networkError != null) {
      appInsights.logException(response.networkError, response.operation);

      let message = "";
      response.graphQLErrors?.map((val) => (message += val.message + "\n"));
      if (response.networkError?.message) {
        message += response.networkError?.message + "\n";
      }

      if (!environment.production) {
        // eslint-disable-next-line no-console
        console.error(message, response);
      }

      messageService.add(this.makeMessage(response.operation.operationName, message));
    }
  }

  private handleSessionExpired(response: ErrorResponse): boolean {
    const networkError = response.networkError as (Error & { error?: string; statusCode?: number }) | null;
    if (!networkError) return false;

    const isExpiredSession =
      (networkError.error != null &&
        ApolloErrorHandlerService.SESSION_EXPIRED_AUTH0_CODES.includes(networkError.error)) ||
      networkError.name === "MissingRefreshTokenError" ||
      networkError.statusCode === 401;
    if (!isExpiredSession) return false;

    if (this.redirectingToLogin) return true;
    this.redirectingToLogin = true;

    const router = this.injector.get(Router);
    const authService = this.injector.get(AuthService);
    const url = router.url;
    const target = url.startsWith("/login") || url.startsWith("/callback") ? undefined : url;
    authService
      .loginWithRedirect({ appState: { target } })
      .pipe(finalize(() => (this.redirectingToLogin = false)))
      .subscribe();
    return true;
  }

  private handleForbidden(error: GraphQLFormattedError): boolean {
    const extensions: Record<string, any> = error.extensions;

    if (extensions?.exception?.status !== 403 && extensions?.code !== ErrorCode.FORBIDDEN) return false;

    const router = this.injector.get(Router);
    void router.navigate(["/accessdenied"], { state: { errorMessage: error.message } });
    return true;
  }

  private handleUserInputError(error: GraphQLFormattedError, response: ErrorResponse): boolean {
    if (error.extensions?.code !== ErrorCode.BAD_USER_INPUT) return false;

    const appInsights = this.injector.get(ApplicationInsightsService);
    appInsights.logEvent("BAD_USER_INPUT");

    const extensions: Record<string, any> = error.extensions;
    const { errorKey, translatedMessages } = this.translateMessages(error);

    if (errorKey) {
      const msgDialogService = this.injector.get(MessageDialogService);
      const reloadService = this.injector.get(ConfigurationReloadService);
      const offersReload =
        [errorMessageKey.SHARINGCONFIGURATIONCHANGED, errorMessageKey.INTERNALMASKCONFIGURATIONCHANGED].includes(
          errorKey
        ) && reloadService.canReload;
      translatedMessages.subscribe((errorMessages) => {
        msgDialogService
          .showMessage(
            translateSafely("user.badInput.title"),
            translateSafely(`graphql.${errorKey}`, errorMessages),
            offersReload ? { label: translateSafely("user.configurationChanged.reload") } : undefined
          )
          .subscribe((tookAction) => {
            if (tookAction) {
              reloadService.requestReload();
            }
          });
      });
    } else {
      const messageService = this.injector.get(MessageService);
      messageService.add(
        this.makeMessage(response.operation.operationName, extensions.response?.message?.join("\n") ?? error.message)
      );
    }

    return true;
  }

  private handleConcurrencyViolation(error: GraphQLFormattedError, response: ErrorResponse): boolean {
    if (error.extensions?.code !== ErrorCode.CONCURRENCY_VIOLATION) {
      return false;
    }

    // Ignore concurrency errors for specific operations
    const ignoredOperations: MutationOperation[] = ["updateDocument", "createDocument"];
    const operationName = response.operation.operationName as MutationOperation;
    if (ignoredOperations.includes(operationName) && response.operation.variables?.input?.ignoreConcurrencyError) {
      return true;
    }

    const appInsights = this.injector.get(ApplicationInsightsService);
    appInsights.logEvent(ErrorCode.CONCURRENCY_VIOLATION);

    const msgDialogService = this.injector.get(MessageDialogService);
    msgDialogService.showMessage(
      translateSafely("user.concurrency.title"),
      translateSafely("user.concurrency.message")
    );

    return true;
  }

  /**
   * The backend turned (part of) the request away under load. Only the call that was turned away is known not to have
   * run: earlier writes of the same action may have been saved (a request is not yet one transaction), so a mutation
   * blocks the page until the user reloads, which shows the saved state. Pressing Save again on the open form would
   * duplicate what was saved. A query cannot duplicate anything and may be a background refresh the user never asked
   * for, so it only gets a toast. One per response, since every field of a query can fail with it at once.
   */
  private handleServiceBusy(error: GraphQLFormattedError, response: ErrorResponse): boolean {
    if (error.extensions?.code !== ErrorCode.SERVICE_BUSY) return false;

    const firstBusyError = response.graphQLErrors?.find((x) => x.extensions?.code === ErrorCode.SERVICE_BUSY);
    if (error !== firstBusyError) return true;

    const appInsights = this.injector.get(ApplicationInsightsService);
    appInsights.logEvent(ErrorCode.SERVICE_BUSY);

    if (this.isMutation(response)) {
      this.promptReload();
      return true;
    }

    const messageService = this.injector.get(MessageService);
    messageService.add({
      severity: "warning",
      summary: translateSafely("user.serviceBusy.title"),
      detail: translateSafely("user.serviceBusy.message"),
    });
    return true;
  }

  private isMutation(response: ErrorResponse): boolean {
    const definition = getMainDefinition(response.operation.query);
    return definition.kind === "OperationDefinition" && definition.operation === "mutation";
  }

  /** At most one dialog, however many mutations were turned away while it is open. */
  private promptReload(): void {
    if (this.isReloadPromptOpen) return;
    this.isReloadPromptOpen = true;

    const documentRef = this.injector.get(DOCUMENT);
    this.injector
      .get(MessageDialogService)
      .showBlockingMessage(
        translateSafely("user.serviceBusy.title"),
        translateSafely("user.serviceBusy.message"),
        translateSafely("user.serviceBusy.reload")
      )
      .subscribe(() => documentRef.location.reload());
  }

  private handleAggregateError(error: GraphQLFormattedError): boolean {
    if (error.extensions?.code !== ErrorCode.AGGREGATEERROR) return false;

    const extensions: Record<string, any> = error.extensions;

    const appInsights = this.injector.get(ApplicationInsightsService);
    appInsights.logEvent(ErrorCode.AGGREGATEERROR);

    const errorMessages: {
      errorKey: errorMessageKey;
      translatedMessages: Observable<Record<string, string>>;
    }[] = extensions.errors?.map((error) => this.translateMessages(error) ?? []);

    const groupedMessages = forkJoin(
      errorMessages.map((x) =>
        x.translatedMessages.pipe(
          map((translatedMessages) => ({
            errorKey: x.errorKey,
            translatedMessages,
          }))
        )
      )
    );

    const msgDialogService = this.injector.get(MessageDialogService);

    groupedMessages.subscribe((groupedMessages) => {
      const errorMessagesString: string[] = [];
      groupedMessages
        .filter((x) => x.errorKey)
        .forEach((messages) => {
          const errorMessage = translateSafely(`graphql.${messages.errorKey}`, messages.translatedMessages);
          errorMessagesString.push(errorMessage);
        });

      if (errorMessagesString.length === 0)
        errorMessagesString.push(extensions.response?.message?.join("\n") ?? error.message);

      msgDialogService.showMessage(translateSafely("user.badInput.title"), errorMessagesString);
    });

    return true;
  }

  private handleAiError(_error: GraphQLFormattedError, response: ErrorResponse): boolean {
    const ignoredOperations: string[] = ["RunAgent", "AgentEvents", "ProcessAgentChat"];
    return ignoredOperations.includes(response.operation.operationName);
  }

  private handleApplicationMaskError(_error: GraphQLFormattedError, response: ErrorResponse): boolean {
    type Operation = MutationOperation | QueryOperation;
    // ignore createCandidateApplicationMask/validation operation - handled separately within the component
    const ignoredOperations: Operation[] = ["createCandidateApplicationMask", "validateApplicationMask"];
    const operationName = response.operation.operationName as Operation;
    return ignoredOperations.includes(operationName);
  }

  private handleVersionError(error: GraphQLFormattedError): boolean {
    if (error.extensions?.code !== ErrorCode.VERSIONERROR) return false;
    // eslint-disable-next-line no-console
    console.log("New updates detected");
    // Can't  be injected in the constructor because of circular dependency or other reasons lost 4 hours on this
    this.injector.get(PromptUpdateService).checkForSoftwareUpdate();
    return true;
  }

  private translateMessages(userInputError: GraphQLFormattedError): {
    errorKey: errorMessageKey | null;
    translatedMessages: Observable<Record<string, string>>;
  } {
    if (userInputError.extensions?.code !== ErrorCode.BAD_USER_INPUT) throw new Error("Not a user input error");

    const extensions: Record<string, any> = userInputError.extensions;

    const properties = (extensions?.properties as { key: string; value: string }[]) ?? [];
    const messagesProperties = properties.filter((x) => x.key && x.value && x.key != "errorMessageKey");
    const errorKey = properties.find((x) => x.key === "errorMessageKey")?.value as errorMessageKey | null;

    const isAlreadyUploadedErrorKey = [
      errorMessageKey.DOCUMENTSALREADYUPLOADED,
      errorMessageKey.NOTARIZATIONSALREADYUPLOADED,
      errorMessageKey.TRANSLATIONSALREADYUPLOADED,
    ].includes(errorKey);

    const staticDataPipe = this.injector.get(StaticDataPipe);

    const translatedMessages = messagesProperties.reduce<
      Observable<{ messageKey: string; translatedMessage: string }>[]
    >((acc, pair: { key: string; value: string }) => {
      let translatedMessage = of(pair.value);
      if (pair.key.startsWith("STATICDATA:")) {
        const [, type, enumKey] = pair.value.split(":");
        translatedMessage = staticDataPipe
          .transform(enumKey, type as StaticDataType)
          .pipe(map((x) => (isAlreadyUploadedErrorKey && pair.key === "details" ? x : `"${x}"`)));
      }
      acc.push(translatedMessage.pipe(map((translatedMessage) => ({ messageKey: pair.key, translatedMessage }))));
      return acc;
    }, []);

    if (translatedMessages.length === 0) {
      return {
        errorKey,
        translatedMessages: of({}),
      };
    }

    return {
      errorKey,
      translatedMessages: forkJoin(translatedMessages).pipe(
        map((x) =>
          zipObject(
            x.map((x) => x.messageKey),
            x.map((x) => x.translatedMessage)
          )
        )
      ),
    };
  }

  private makeMessage(summary: string, detail: string): Message {
    return { severity: "error", summary: summary, detail: detail };
  }
}
